// LeaderboardCategory.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SwipeableViews from 'react-swipeable-views';
import { parseISO, format } from 'date-fns';
import { useLocation } from 'react-router';

import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import LeaderboardCategoryUsers from '../container/LeaderboardCategoryUsers.js';
import DecryptionWrapper from '../container/DecryptionWrapper.js';
import { headerHeight, color, textColor } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import {
  RANGE_24H,
  RANGE_7D,
  RANGE_30D,
} from '../resource/leaderboardConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

import crownIcon from '../../img/ic_crown_16.svg';
import backIcon from '../../img/ic_topbar_back.svg';
import dropdownIcon from '../../img/btn-dropdown-white.svg';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

// leaderboard will update at 00:00 UTC+8, and should convert to local time
const UPDATED_AT = format(parseISO('2019-07-03T00:00:00+08:00'), 'HH:mm');

const LEADERBOARD_CATEGORY_HEIGHT = 40;
const HORIZONTAL_GAP = 16;
const DROPDOWN_ITEM_HEIGHT = 48;

const tabsStyle = {
  container: isMobile => ({
    overflowX: 'auto',
    paddingBottom: '20px',
    justifyContent: isMobile ? 'flex-start' : 'center',
  }),
};
const tabStyle = {
  flex: 'none',
  padding: '0',
  marginRight: '24px',
};
const lastTabStyle = {
  ...tabStyle,
  marginRight: '0px',
};
const tabLabelStyle = {
  margin: '0px',
  padding: '12px 6px',
};
const indicatorStyle = {
  bottom: '19px',
};
const slideStyle = {};
const isClient = typeof window !== 'undefined';

const LeaderboardCategory = ({
  goBack = () => null,
  replace = () => null,
  deviceWidth = 0,
  categories = [],
  regions = [],
  publicLeaderboardUrlPath = '',
}) => {
  const { t } = useTranslation();
  const {
    region: activeRegion,
    range: activeRange,
    category: activeCategory,
  } = useParams();
  const [regionCandidates, setRegionCandidates] = useState([]);
  const [isOpened, setIsOpened] = useState(false);
  let swipeableActions = null;
  const tabsRef = useRef(null);
  const { search } = useLocation();

  useEffect(() => {
    setRegionCandidates(regions.filter(region => region !== activeRegion));
  }, [activeRegion, regions]);

  const changeCondition = ({ category, range, region }) => {
    if (isClient) {
      // make sure always on top
      window.scrollTo(0, 0);
    }
    if (isOpened) {
      setIsOpened(false);
    }

    replace({
      pathname: `/leaderboard/${region}/${category}/${range}`,
    });

    if (swipeableActions) {
      swipeableActions.updateHeight();
    }
  };

  const toggleDropdown = () => {
    setIsOpened(isOpened => !isOpened);
  };

  const getTabHref = useCallback(
    ({ category, region, range }) => {
      return `/leaderboard/${region}/${category}/${range}${search}`;
    },
    [search]
  );

  let updatedHint;

  if (activeRange === RANGE_24H) {
    updatedHint = t('leaderboard_update_by_hour_hint', {
      ns: TranslationNamespace.FEED,
    });
  } else {
    updatedHint = t('leaderboard_update_by_day_hint', {
      updatedAt: UPDATED_AT,
      ns: TranslationNamespace.FEED,
    });
  }

  return (
    <StyledLeaderboardCategory>
      <HeaderWrapper>
        <Header>
          <GoBack onClick={goBack}>
            <img
              src={backIcon}
              title="go back"
              alt="go back"
              width="32"
              height="32"
            />
          </GoBack>
          <DropdownButton onClick={toggleDropdown}>
            <CrownIcon src={crownIcon} alt="crown icon" />
            <DropdownButtonText>
              {t(`leaderboard_${activeRegion}`, {
                ns: TranslationNamespace.FEED,
              })}
            </DropdownButtonText>
            <DropdownIcon
              src={dropdownIcon}
              isOpened={isOpened}
              alt="dropdown icon"
            />
          </DropdownButton>
          <Placehlder></Placehlder>
        </Header>
        <DropdownListWrapper
          listHeight={
            isOpened ? regionCandidates.length * DROPDOWN_ITEM_HEIGHT : 0
          }
        >
          {regionCandidates.map(region => {
            return (
              <DropdownItem
                key={region}
                value={t(`leaderboard_${region}`, {
                  ns: TranslationNamespace.FEED,
                })}
                onClick={() =>
                  changeCondition({
                    category: activeCategory,
                    range: activeRange,
                    region,
                  })
                }
              >
                {t(`leaderboard_${region}`, {
                  ns: TranslationNamespace.FEED,
                })}
              </DropdownItem>
            );
          })}
        </DropdownListWrapper>
        <TabsWrapper>
          <Tabs
            ref={tabsRef}
            indicatorStyle={indicatorStyle}
            style={tabsStyle.container(
              deviceWidth < tabsRef.current?.tabsRef.scrollWidth
            )}
            value={activeCategory}
            onChange={(event, category) => {
              event?.preventDefault();
              changeCondition({
                category: category,
                range: RANGE_24H,
                region: activeRegion,
                buttonId: ButtonId.Leaderboard.ButtonLeaderboardTab,
              });
            }}
            isScrollable
          >
            {categories.map((category, index) => {
              const iconSource = getResourceUrl({
                path: publicLeaderboardUrlPath,
                endpoint: `/${category}.png`,
                resourceType: PUBLIC,
              });
              const [avifSrcSet, webpSrcSet, imgSrcSet] = [
                MediaAssetFormat.AVIF,
                MediaAssetFormat.WEBP,
                MediaAssetFormat.PNG, // (2024.09) There's no JPG format for this resource
              ].map(format =>
                getPublicSrcSet({ href: iconSource, size: 64, format })
              );

              const isLastItem = index === categories.length - 1;
              return (
                <Tab
                  data-element_id={ButtonId.Leaderboard.ButtonLeaderboardTab}
                  data-tracking_payload={{
                    'leaderboard.region': activeRegion,
                    'leaderboard.tab': category,
                    'leaderboard.range': RANGE_24H,
                  }}
                  key={category}
                  title={category}
                  style={isLastItem ? lastTabStyle : tabStyle}
                  labelStyle={tabLabelStyle}
                  value={category}
                  href={getTabHref({
                    category,
                    region: activeRegion,
                    range: RANGE_24H,
                  })}
                  label={
                    <TabLabel isActive={category === activeCategory}>
                      <picture width="14" height="14">
                        <DecryptionWrapper resourceUrl={imgSrcSet.src}>
                          <source
                            srcSet={avifSrcSet.srcSet}
                            type="image/avif"
                          />
                          <source
                            srcSet={webpSrcSet.srcSet}
                            type="image/webp"
                          />
                          <img {...imgSrcSet} alt={`${category} icon`} />
                        </DecryptionWrapper>
                      </picture>
                      {t(`leaderboard_${category}`, {
                        ns: TranslationNamespace.FEED,
                      })}
                    </TabLabel>
                  }
                />
              );
            })}
          </Tabs>
        </TabsWrapper>
      </HeaderWrapper>
      <Body>
        <TimeRangeWrapper>
          <TimeRangeButton
            isActive={activeRange === RANGE_24H}
            color={color.peach}
            data-element_id={ButtonId.All.TabLeaderboardHour}
            data-tracking_payload={{
              'leaderboard.region': activeRegion,
              'leaderboard.tab': activeCategory,
              'leaderboard.range': RANGE_24H,
            }}
            onClick={() => {
              changeCondition({
                category: activeCategory,
                range: RANGE_24H,
                region: activeRegion,
              });
            }}
          >
            <RangeTextWrapper>
              {t('leaderboard_range_24h', {
                ns: TranslationNamespace.FEED,
              })}
            </RangeTextWrapper>
          </TimeRangeButton>
          <TimeRangeButton
            isActive={activeRange === RANGE_7D}
            color={color.green}
            data-element_id={ButtonId.All.TabLeaderboardWeek}
            data-tracking_payload={{
              'leaderboard.region': activeRegion,
              'leaderboard.tab': activeCategory,
              'leaderboard.range': RANGE_7D,
            }}
            onClick={() => {
              changeCondition({
                category: activeCategory,
                range: RANGE_7D,
                region: activeRegion,
              });
            }}
          >
            <RangeTextWrapper>
              {t('leaderboard_range_7d', {
                ns: TranslationNamespace.FEED,
              })}
            </RangeTextWrapper>
          </TimeRangeButton>
          <TimeRangeButton
            isActive={activeRange === RANGE_30D}
            color={color.sky}
            data-element_id={ButtonId.All.TabLeaderboardMonth}
            data-tracking_payload={{
              'leaderboard.region': activeRegion,
              'leaderboard.tab': activeCategory,
              'leaderboard.range': RANGE_30D,
            }}
            onClick={() => {
              changeCondition({
                category: activeCategory,
                range: RANGE_30D,
                region: activeRegion,
              });
            }}
          >
            <RangeTextWrapper>
              {t('leaderboard_range_30d', {
                ns: TranslationNamespace.FEED,
              })}
            </RangeTextWrapper>
          </TimeRangeButton>
        </TimeRangeWrapper>
        <Update>{updatedHint}</Update>
        <SwipeableViews
          index={categories.indexOf(activeCategory)}
          onChangeIndex={index => {
            changeCondition({
              category: categories[index],
              range: RANGE_24H,
              region: activeRegion,
            });
          }}
          enableMouseEvents
          slideStyle={slideStyle}
          action={actions => {
            swipeableActions = actions;
          }}
        >
          {categories.map(category => {
            const isActive = activeCategory === category;
            return (
              <LeaderboardCategoryUsers
                key={category}
                region={activeRegion}
                category={category}
                range={activeRange}
                isActive={isActive}
              />
            );
          })}
        </SwipeableViews>
      </Body>
    </StyledLeaderboardCategory>
  );
};

LeaderboardCategory.propTypes = {
  goBack: PropTypes.func,
  replace: PropTypes.func,
  categories: PropTypes.array,
  regions: PropTypes.array,
  deviceWidth: PropTypes.number,
  publicLeaderboardUrlPath: PropTypes.string,
};

const StyledLeaderboardCategory = styled.div`
  position: relative;
  height: 100vh;
  padding-top: var(--space-to-pinned-notification);
`;

const HeaderWrapper = styled.div`
  position: fixed;
  z-index: 1;
  width: 100%;
`;

const Header = styled.header`
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${headerHeight}px;
  top: var(--space-to-pinned-notification);
  background-color: ${color.grey[900]};
`;

const GoBack = styled.button`
  ${ResetButtonStyle}
  width: 32px;
  height: 32px;
  :hover {
    opacity: 0.7;
  }
  & > img {
    height: 100%;
    vertical-align: top;
  }
`;

const DropdownButton = styled.button`
  ${ResetButtonStyle}
  flex: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  margin-left: 12px;
`;

const DropdownButtonText = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  ${TextEllipsis};
`;

const CrownIcon = styled.img`
  margin-right: 6px;
  border-radius: 50%;
  padding: 2px;
  flex: none;
  width: 20px;
  height: 20px;
  background-color: ${color.yellow};
`;

const Placehlder = styled.div`
  flex: none;
  width: 32px;
`;

const DropdownIcon = styled.img.attrs(({ isOpened }) => ({
  style: {
    transform: `rotate(${isOpened ? '180' : '0'}deg)`,
  },
}))`
  margin-left: 4px;
  flex: none;
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
`;

const DropdownListWrapper = styled.div.attrs(({ listHeight }) => ({
  style: {
    height: `${listHeight}px`,
  },
}))`
  position: absolute;
  top: ${headerHeight}px;
  left: 0;
  right: 0;
  z-index: 1;
  transition: height 0.3s;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  ${ResetButtonStyle}
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  width: 100%;
  height: ${DROPDOWN_ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${textColor.black};
  font-size: 16px;
  font-weight: 600;
  background-color: ${color.grey[900]};
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid ${color.neutral['30_alpha_50']};
  height: ${LEADERBOARD_CATEGORY_HEIGHT}px;
  background-color: ${color.grey[900]};
  overflow-y: hidden;
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${color.white};
  line-height: 1;
  > picture {
    margin-right: 8px;
    width: 14px;
    height: 14px;

    > img {
      width: 100%;
      height: 100%;
    }
  }
`;

const Body = styled.main`
  padding-top: ${headerHeight + LEADERBOARD_CATEGORY_HEIGHT}px;
`;

const TimeRangeWrapper = styled.div`
  margin-left: auto;
  margin-right: auto;
  padding: 24px ${HORIZONTAL_GAP}px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 375px;
`;

const TimeRangeButton = styled.button`
  ${ResetButtonStyle}
  border: ${({ color }) => `1px solid ${color}`};
  border-radius: 12px;
  padding: 0px 12px;
  width: 100px;
  height: 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background-color: ${({ isActive, color }) =>
    isActive ? color : color.black};
  color: ${({ isActive, color }) => (isActive ? textColor.white : color)};
  @media (max-width: 320px) {
    width: 80px;
  }
`;

const RangeTextWrapper = styled.h2`
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  ${TextEllipsis}
  line-height: 1;
`;

const Update = styled.div`
  ${TextEllipsis}
  padding: 0px ${HORIZONTAL_GAP}px 4px;
  font-size: 12px;
  color: ${textColor.black};
  text-align: right;
`;

export default LeaderboardCategory;
